import type { EditorAdapter } from '../adapters/adapter'
import { DragHandle } from '@tiptap/extension-drag-handle-react'
import { EditorContent } from '@tiptap/react'
import { useEffect, useRef } from 'react'
import { EditorContext } from '../store/context'
import { useEditorStore } from '../store/editor-store'
import { useViewStore } from '../store/view-store'
import { ImageDialog } from './ImageDialog'
import { IssueSuggestionPopup } from './IssueSuggestionPopup'
import { LinkBubble } from './LinkBubble'
import { LinkDialog } from './LinkDialog'
import { MacroSuggestionPopup } from './MacroSuggestionPopup'
import { PreviewPane } from './PreviewPane'
import { Toolbar } from './toolbar/Toolbar'
import { UploadOverlay } from './UploadOverlay'
import { UserMentionPopup } from './UserMentionPopup'
import { WikiLinkBubble } from './WikiLinkBubble'
import { WikiLinkDialog } from './WikiLinkDialog'
import { WikiLinkSuggestionPopup } from './WikiLinkSuggestionPopup'
import './editor.css'

interface Props {
  adapter: EditorAdapter
  textarea: HTMLTextAreaElement
}

export function RedmineTiptap({ adapter, textarea }: Props) {
  const view = useViewStore()

  // useEditorStore/useEditor build the editor once on mount — the onUpdate
  // callback passed in is captured at that point and never recreated, so it
  // must read live state through a ref rather than closing over `view`/`store`
  // directly (which would otherwise always see their first-render values).
  const liveRef = useRef({ sourceMode: view.sourceMode, ready: false })

  const store = useEditorStore({
    adapter,
    initialValue: textarea.value,
    onUpdate: (md: string) => {
      if (!liveRef.current.sourceMode)
        textarea.value = md
    },
  })

  liveRef.current = { sourceMode: view.sourceMode, ready: store.ready }

  useEffect(() => {
    if (view.sourceMode && store.ready)
      textarea.value = store.editor().getMarkdown()
  }, [view.sourceMode, store.ready, store, textarea])

  useEffect(() => {
    const ctrl = new AbortController()
    textarea.addEventListener('input', () => {
      if (!liveRef.current.sourceMode || !liveRef.current.ready)
        return

      store.updateContent(textarea.value)
    }, { signal: ctrl.signal })

    return () => {
      ctrl.abort()
      store.destroy()
    }
    // eslint-disable-next-line react/exhaustive-deps
  }, [])

  return (
    <EditorContext value={{ view, store }}>
      <div className={`redmine-tiptap-editor${view.fullscreen ? ' is-fullscreen' : ''}`}>
        <div className={`tiptap-editor-container${view.viewMode === 'split' ? ' is-split' : ''}`}>
          <Toolbar />

          <div className="tiptap-editor-body">
            <div
              className={[
                'tiptap-editor-wrapper',
                view.sourceMode ? 'is-source-mode' : '',
                view.viewMode === 'preview' ? 'hidden' : '',
              ].filter(Boolean).join(' ')}
            >
              <EditorContent
                editor={store.rawEditor}
                className="tiptap-editor"
                style={{ display: view.sourceMode ? 'none' : undefined }}
              />
              {/* Textarea lives in the shadow host's light DOM; shown here in source mode via ::slotted */}
              <slot />
            </div>

            <PreviewPane />
          </div>

          {store.ready && (
            <DragHandle editor={store.editor()}>
              <div className="custom-drag-handle" />
            </DragHandle>
          )}

          <UploadOverlay />
          <ImageDialog />
          <LinkDialog />
          <LinkBubble />
          <WikiLinkDialog />
          <WikiLinkBubble />
        </div>

        {/* Suggestion popups — rendered outside the editor container so they
                    can overflow and use CSS anchor positioning against the caret */}
        <IssueSuggestionPopup />
        <WikiLinkSuggestionPopup />
        <MacroSuggestionPopup />
        <UserMentionPopup />
      </div>
    </EditorContext>
  )
}
